import { useEffect, useState } from 'react'

interface UseScrollspyOptions {
  /**
   * Array of element IDs to observe
   */
  items: string[]
  /**
   * Offset from the top of the viewport (in pixels).
   * The active section is the last one whose top has crossed this line.
   */
  offset?: number
  /**
   * @deprecated Position-based scrollspy ignores IntersectionObserver rootMargin.
   */
  rootMargin?: string
  /**
   * @deprecated Position-based scrollspy ignores IntersectionObserver threshold.
   */
  threshold?: number | number[]
  /**
   * Whether to pause active item detection (e.g., during manual scroll)
   */
  isPaused?: boolean
}

/**
 * Scrollspy that tracks which section currently owns the offset line.
 * Uses measured heading position (last heading with top <= offset) so scrolling
 * up and down update the active item as soon as content crosses the line — not
 * only when a heading re-enters an IntersectionObserver root. Selection does
 * not depend on `items` order matching DOM order.
 */
export function useScrollspy({
  items,
  offset = 0,
  isPaused = false,
}: UseScrollspyOptions): string | null {
  const [activeItem, setActiveItem] = useState<string | null>(null)

  useEffect(() => {
    if (isPaused || items.length === 0) return

    let frameId = 0

    const updateActiveItem = () => {
      const measured = items
        .map((id) => ({ id, top: document.getElementById(id)?.getBoundingClientRect().top }))
        .filter((entry): entry is { id: string; top: number } => entry.top !== undefined)
        .sort((a, b) => a.top - b.top)

      const crossed = measured.filter((entry) => entry.top <= offset)
      const nextItem = crossed.at(-1)?.id ?? measured[0]?.id ?? null
      setActiveItem((prev) => (prev === nextItem ? prev : nextItem))
    }

    const onScrollOrResize = () => {
      if (frameId) return
      frameId = window.requestAnimationFrame(() => {
        frameId = 0
        updateActiveItem()
      })
    }

    updateActiveItem()
    window.addEventListener('scroll', onScrollOrResize, { passive: true })
    window.addEventListener('resize', onScrollOrResize)

    return () => {
      window.cancelAnimationFrame(frameId)
      window.removeEventListener('scroll', onScrollOrResize)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [items, offset, isPaused])

  return activeItem
}
